/**
 * ACTIVITY-CORE — pure feed presentation helpers (UI-agnostic).
 * Human-facing text (i18n) lives in the app; here it's structure only:
 * patch parsing, batch grouping and "done offline" detection.
 *
 * ONE SINGLE PATH AND ONE SINGLE FORMAT (plan 06, E5). The feed is served via
 * `GET /api/activity`, so the feed row IS the DTO generated from
 * `db/activity.rs`. Until 2026-07-27 it coexisted with the snake_case row of the
 * local SQLite `activity_recent` mirror and a hand-written adapter between the
 * two (`activity-feed.tsx`); that second path no longer exists.
 */
import type { components } from "@space/client";

/** Feed row = master log entry + whether the API accepts it in `/revert`. */
export type ActivityRow = components["schemas"]["ActivityFeedEntry"];

export type ActivityAction = "created" | "updated" | "deleted" | "restored" | "reverted";

export interface ActivityFieldChange {
  old: unknown;
  new: unknown;
}

export type ActivityPatch = Record<string, ActivityFieldChange>;

/** Type guard for an `{old, new}` change (no unsafe assertions). */
function isFieldChange(value: unknown): value is { old?: unknown; new?: unknown } {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Normalizes the DTO's jsonb `patch` to `{field: {old, new}}`. Never throws: the
 * action vocabulary is extended by the server, and one weird entry must not be able to
 * take down the feed.
 */
export function parseActivityPatch(raw: unknown): ActivityPatch {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return {};
  }
  const patch: ActivityPatch = {};
  for (const [field, change] of Object.entries(raw)) {
    if (isFieldChange(change)) {
      patch[field] = { old: change.old, new: change.new };
    }
  }
  return patch;
}

/** Changed fields of an entry, in stable order. */
export function changedFields(patch: ActivityPatch): string[] {
  return Object.keys(patch).toSorted();
}

/**
 * Threshold above which a clientTs↔serverTs difference is considered
 * "done offline and synced later" (rather than mere network latency).
 */
export const OFFLINE_LAG_THRESHOLD_MS = 60_000;

/**
 * Milliseconds between when the client made the change (clientTs)
 * and its arrival at the server (serverTs). `null` if either is missing or fails to parse.
 */
export function activityLagMs(row: Pick<ActivityRow, "serverTs" | "clientTs">): number | null {
  if (!row.serverTs || !row.clientTs) {
    return null;
  }
  const server = Date.parse(row.serverTs);
  const client = Date.parse(row.clientTs);
  if (Number.isNaN(server) || Number.isNaN(client)) {
    return null;
  }
  return server - client;
}

/** Was it done offline? (the client stamped it well before it reached the server) */
export function isOfflineEdit(
  row: Pick<ActivityRow, "serverTs" | "clientTs">,
  thresholdMs: number = OFFLINE_LAG_THRESHOLD_MS,
): boolean {
  const lag = activityLagMs(row);
  return lag !== null && lag > thresholdMs;
}

export interface ActivityBatchGroup<T extends ActivityRow = ActivityRow> {
  /** Common batchId (or the row's id if it has no batch). */
  batchId: string;
  entries: T[];
}

/**
 * Groups CONSECUTIVE feed rows (already ordered serverTs DESC) by batchId:
 * "imported 40 tasks" renders as one group, not 40 cards. Does not reorder.
 */
export function groupActivityByBatch<T extends ActivityRow>(
  rows: readonly T[],
): ActivityBatchGroup<T>[] {
  const groups: ActivityBatchGroup<T>[] = [];
  for (const row of rows) {
    const key = row.batchId ?? row.id;
    const last = groups.at(-1);
    if (last && last.batchId === key) {
      last.entries.push(row);
    } else {
      groups.push({ batchId: key, entries: [row] });
    }
  }
  return groups;
}
